import { useState } from "react";
import { Menu, X, Smartphone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "./Logo";
import { OpenAppButton, WaitlistButton } from "./Buttons";
import { scrollToId, goToTesterSignup } from "@/lib/site";

const LINKS = [
  ["features", "Features"],
  ["how", "How it works"],
  ["screens", "The app"],
  ["pricing", "Pricing"],
  ["faq", "FAQ"],
];

const NavLink = ({ id, label, onClick }) => (
  <button
    type="button"
    data-testid={`nav-link-${id}`}
    onClick={() => { scrollToId(id); onClick?.(); }}
    className="text-left text-sm text-[#c4c8cf] transition-colors duration-300 hover:text-[#ff8a3d]"
  >
    {label}
  </button>
);

const TesterPill = ({ testId, onClick, className = "", full = false }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={() => { goToTesterSignup(); onClick?.(); }}
    className={`inline-flex items-center gap-1.5 rounded-full border border-[#4ade80]/30 bg-[#4ade80]/[0.07] px-3.5 py-2 text-sm text-[#d1fae5] transition-[border-color,background-color] duration-300 hover:border-[#4ade80]/60 hover:bg-[#4ade80]/[0.12] ${className}`}
  >
    <Smartphone className="h-3.5 w-3.5 text-[#4ade80]" />
    {full ? <span>Android testers wanted</span> : <>
      <span className="hidden xl:inline">Android testers wanted</span>
      <span className="xl:hidden">Testers wanted</span>
    </>}
  </button>
);

export const Nav = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/[0.06] bg-[#0e0f12]/75 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Logo />
        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map(([id, l]) => <NavLink key={id} id={id} label={l} />)}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <TesterPill testId="navbar-tester-link" />
          <WaitlistButton testId="navbar-waitlist-btn" label="Waitlist" className="!px-5 !py-2.5" />
          <OpenAppButton testId="navbar-open-app-btn" label="Open app" className="!px-5 !py-2.5" />
        </div>
        <button data-testid="navbar-mobile-toggle" className="lg:hidden" onClick={() => setOpen((o) => !o)} aria-label="Menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="navbar-mobile-menu"
            initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }}
            className="overflow-hidden border-t border-white/[0.06] lg:hidden"
          >
            <div className="flex flex-col gap-5 px-5 py-6">
              {LINKS.map(([id, l]) => <NavLink key={id} id={id} label={l} onClick={() => setOpen(false)} />)}
              <TesterPill testId="navbar-mobile-tester-link" onClick={() => setOpen(false)} className="self-start" full />
              <OpenAppButton testId="navbar-mobile-open-app-btn" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
